"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.futureSymbolFormatter = exports.spotSymbolFormatter = exports.FutureSymbolFormatter = exports.SpotSymbolFormatter = void 0;
const trade_utils_lib_1 = require("trade_utils_lib");
const PicolUtils_1 = require("./PicolUtils");
const utils_1 = require("./utils");
class SymbolDataFormatter {
    // map处理函数
    get symbolMaps() {
        return this.customMaps;
    }
    // filter过滤函数
    get symbolFilters() {
        return [this.symbolFilter].concat(this.customFilters);
    }
    /**
     * 手动注入map函数，请确保项目生命周期内一次性注入，多次调用会执行覆盖操作
     * @param fn 需要注入的map函数，单个函数或者函数数组
     */
    injectMapsFn(fn) {
        this.resetCustomMaps();
        const fns = Array.isArray(fn) ? fn : [fn];
        this.customMaps.push(...fns);
    }
    /**
     * 手动注入filter函数，请确保项目生命周期内一次性注入，多次调用会执行覆盖操作
     * @param fn 需要注入的filter函数，单个函数或者函数数组
     */
    injectFiltersFn(fn) {
        this.resetCustomFilters();
        const fns = Array.isArray(fn) ? fn : [fn];
        this.customFilters.push(...fns);
    }
    // 对原始数组过滤
    symbolFilter(symbol) {
        const productType = (0, trade_utils_lib_1.getTradeDependencyInstance)().getProductType();
        const isPicol = productType === trade_utils_lib_1.PRODUCT_TYPE.PICOL;
        return !(isPicol &&
            (PicolUtils_1.PicolUtils.isPicolForbidSymbol(symbol) ||
                PicolUtils_1.PicolUtils.isPicolForbidCoin(symbol.symbol) ||
                PicolUtils_1.PicolUtils.isPicolForbidCoin(symbol.quote)));
    }
    // 聚合的过滤处理
    mergeFilter() {
        return (symbol) => this.symbolFilters.reduce((pre, cur) => pre && cur(symbol), true);
    }
    // 聚合的map处理
    mergeMap() {
        return (symbol) => this.symbolMaps.reduce((pre, cur) => cur(pre), symbol);
    }
    // 将接口返回的原始数据格式化成ExchangeSymbolData
    toSymbolData(symbolType, data, format = true) {
        const symbolMap = new Map();
        const quotes = new Set([]);
        // 将数据做一层过滤和一层map
        const formattedData = format ? data.filter(this.mergeFilter()).map(this.mergeMap()) : data;
        formattedData.forEach((symbol) => {
            symbolMap.set(symbol.symbol, symbol);
            quotes.add(symbol.quote);
        });
        return {
            symbolType,
            symbols: formattedData,
            map: symbolMap,
            quotes: (0, utils_1.sortQuotes)(Array.from(quotes)),
        };
    }
}
// 现货symbol数据处理
class SpotSymbolFormatter extends SymbolDataFormatter {
    constructor() {
        super(...arguments);
        this.customMaps = [this.spotSymbolMap];
        this.customFilters = [this.spotSymbolFilter];
    }
    resetCustomMaps() {
        this.customMaps = [this.spotSymbolMap];
    }
    resetCustomFilters() {
        this.customFilters = [this.spotSymbolFilter];
    }
    spotSymbolMap(symbol) {
        var _a, _b, _c, _d, _e;
        const display = (0, utils_1.parseSpotSymbolInfo)(symbol);
        return Object.assign({ tags: symbol.tags, symbol: symbol.symbol, base: symbol.base, quote: symbol.quote, baseDisplay: display.baseDisplay, quoteDisplay: display.quoteDisplay, isLeverage: display.isLeverage, symbolType: display.type, isSpot: true, precision: Math.abs((_b = (_a = symbol.precision) === null || _a === void 0 ? void 0 : _a.price) !== null && _b !== void 0 ? _b : 8), amount: Math.abs((_d = (_c = symbol.precision) === null || _c === void 0 ? void 0 : _c.amount) !== null && _d !== void 0 ? _d : 8), precisionCost: Math.abs((_e = symbol.precision) === null || _e === void 0 ? void 0 : _e.cost) }, (symbol.limits
            ? {
                limit: {
                    amount: symbol.limits.amount,
                    price: symbol.limits.price,
                    cost: symbol.limits.cost || { max: null, min: null },
                    spending: symbol.limits.spending || { max: null, min: null },
                    dumping: symbol.limits.dumping || { max: null, min: null },
                },
            }
            : {}));
    }
    // 对原始数组过滤
    spotSymbolFilter(symbol) {
        return symbol.active !== false;
    }
}
exports.SpotSymbolFormatter = SpotSymbolFormatter;
// 合约symbol数据处理
class FutureSymbolFormatter extends SymbolDataFormatter {
    constructor() {
        super(...arguments);
        this.customMaps = [this.futureSymbolMap];
        this.customFilters = [];
    }
    resetCustomMaps() {
        this.customMaps = [this.futureSymbolMap];
    }
    resetCustomFilters() {
        this.customFilters = [];
    }
    // 对原始数据进行字段处理
    futureSymbolMap(symbol) {
        const displaySymbolArr = symbol.display_symbol.split("_");
        const baseDisplay = displaySymbolArr.length ? displaySymbolArr[0] : symbol.base;
        let quoteDisplay = displaySymbolArr.length > 1 ? displaySymbolArr[1] : "";
        // TODO 先mock
        // 如果开启了“隐藏模拟模式”，并且当前是“虚拟币”，那么直接返回 "USDT"
        // if (hiddenDemoMode.isHiddenDemoMode && quoteDisplay === "PUSD") {
        //     quoteDisplay = "USDT";
        // }
        return {
            tags: symbol.tags,
            symbol: symbol.symbol,
            base: symbol.base,
            quote: symbol.quote,
            baseDisplay,
            quoteDisplay,
            isLeverage: false,
            precision: symbol.quote_precision,
            amount: symbol.base_precision,
            precisionCost: symbol.spending_precision,
            isSpot: false,
            limit: {
                amount: {
                    min: symbol.min_qty_limit ? Number(symbol.min_qty_limit) : null,
                    max: symbol.max_qty_limit ? Number(symbol.max_qty_limit) : null,
                },
                cost: { min: symbol.min_notional ? Number(symbol.min_notional) : null, max: null },
                price: { min: null, max: null },
                spending: {
                    min: symbol.min_notional ? Number(symbol.min_notional) : null,
                    max: symbol.max_spending ? Number(symbol.max_spending) : null,
                },
                dumping: {
                    min: symbol.min_qty_market ? Number(symbol.min_qty_market) : null,
                    max: symbol.max_qty_market ? Number(symbol.max_qty_market) : null,
                },
            },
            symbolType: trade_utils_lib_1.ExchangeSymbolType.future_perpetual,
            originData: symbol,
        };
    }
}
exports.FutureSymbolFormatter = FutureSymbolFormatter;
// 现货数据formatter单例，供外部调用增加自定义数据处理
exports.spotSymbolFormatter = new SpotSymbolFormatter();
// 合约数据formatter单例，供外部调用增加自定义数据处理
exports.futureSymbolFormatter = new FutureSymbolFormatter();
